
import type { BillingInterval } from '@shared/billing';
import { graphql, gqlRequest } from '@client/shared/graphql';

export type { BillingInterval };

export const BILLING_STATUS_QUERY_KEY = ['billing', 'status'] as const;

export const BillingStatusQuery = graphql(`
  query BillingStatus {
    billingStatus {
      enabled
      subscriptionStatus
      trialEndsAt
      inGoodStanding
      activeListings
      maxActiveListings
      postTrialCap
    }
  }
`);

// Normalized once here: the generated schema types every field nullable (Pothos default),
// but the resolver always returns them.
export interface BillingStatus {
  /** False on deployments running with billing switched off — hide all billing UI. */
  readonly enabled: boolean;
  readonly subscriptionStatus: string | null;
  readonly trialEndsAt: string;
  readonly inGoodStanding: boolean;
  readonly activeListings: number;
  readonly maxActiveListings: number;
  /** What the group keeps if the trial lapses unsubscribed (override-aware Free cap). */
  readonly postTrialCap: number;
}

export async function fetchBillingStatus(): Promise<BillingStatus> {
  const data = await gqlRequest(BillingStatusQuery);
  const s = data.billingStatus;
  if (
    !s ||
    s.enabled == null ||
    s.trialEndsAt == null ||
    s.inGoodStanding == null ||
    s.activeListings == null ||
    s.maxActiveListings == null ||
    s.postTrialCap == null
  ) {
    throw new Error('Couldn’t load billing status. Try again.');
  }
  return s as BillingStatus;
}

export const CheckoutSessionMutation = graphql(`
  mutation CreateBillingCheckoutSession($interval: String!) {
    createBillingCheckoutSession(interval: $interval) {
      url
    }
  }
`);

export async function createCheckoutUrl(interval: BillingInterval): Promise<string> {
  const data = await gqlRequest(CheckoutSessionMutation, { interval });
  const url = data.createBillingCheckoutSession?.url;
  if (!url) throw new Error('Couldn’t open Stripe Checkout. Try again.');
  return url;
}

export const PortalSessionMutation = graphql(`
  mutation CreateBillingPortalSession {
    createBillingPortalSession {
      url
    }
  }
`);

/** Full-page redirect to a Stripe-hosted page; separated so tests can stub the
 *  navigation jsdom can't perform. */
export function redirectToStripe(url: string): void {
  window.location.assign(url);
}

export async function createPortalUrl(): Promise<string> {
  const data = await gqlRequest(PortalSessionMutation);
  const url = data.createBillingPortalSession?.url;
  if (!url) throw new Error('Couldn’t open the billing portal. Try again.');
  return url;
}
